
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRef } from 'react';
import { FiDownload, FiMail, FiPhone } from 'react-icons/fi';
import styled from 'styled-components';

import nextI18NextConfig from '../../next-i18next.config.js';
import PreviewPress from '@/components/blocks/PreviewPress';

import BackgroundImage from '@/components/molecules/BackgroundImage';
import Button from '@/components/molecules/Button.jsx';
import SectionText from '@/components/molecules/SectionText';
import Title from '@/components/molecules/Title.jsx';
import IntroSection from '@/components/wrappers/IntroSection';
import PageWrapper from '@/components/wrappers/PageWrapper';
import SplitSection from '@/components/wrappers/SplitSection';

import devices from '@/utilities/devices';
import { renderScript, renderUnderline } from '@/utilities/functions';

// #region STYLE
const PreviewPressContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row !important;
  overflow: hidden;
  background-color: ${props => props.theme.color.black};
  @media screen and (${devices.mobile}) {
    flex-direction: column !important;
  }
`;

const DownloadResourcesContainer = styled.div`
  text-align: center;
  @media screen and (${devices.mobile}) {
    padding: 1em 0 2em;
  }
  @media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
    padding: 1em 0 2em;
  }
`;

const DownloadResourcesButton = styled.a`
  width: 60%;
  margin: .4em auto;
  padding: .1em 0;
  text-decoration: none;
  font-size: 1.4em;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media screen and (${devices.mobile}) {
    font-size: 1em;
  }
  @media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
    font-size: 1.2em;
  }

  div {
    color: ${props => props.theme.color.white};
    transition: .3s;
  }

  svg {
    color: ${props => props.theme.color.white};
    transition: .3s;
  }

  :hover {
    div {
      color: ${props => props.theme.color.prune};
    }
    svg {
      color: ${props => props.theme.color.prune};
    }
  }
`;

const LogoPressContainer = styled.div`
  flex-direction: row !important;
  align-items: center !important;
  justify-content: center;
  flex-wrap: wrap;
  a {
    width: 33.3333%;
    align-items: baseline;
    justify-content: center;
    padding: 1em 0;
    text-decoration: none;
    transition: .2s;
    :hover {
      transform: translateY(-3px);
    }
    img {
      display: block;
      width: 80%;
      margin: auto;
    }
  }
  @media screen and (${devices.mobile}) {
    padding: 2em 0;
    a img {
      width: 50%;
    }
  }
  @media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
    padding: 2em 0;
    a img {
      width: 60%;
    }
  }
`;

const InfoContact = styled.div`
  .contact-name {
    font-size: .65em;
    color: ${props => props.theme.color.prune};
    padding: 1em 0 .2em;
    @media screen and (${devices.mobile}) {
      padding: 0em;
    }
  }

  .contact-mail {
    display: flex;
    align-items: center;
    font-size: .55em;
    svg {
      flex-shrink: 0;
      font-size: 1em;
      padding-right: .7em;
    }
    @media screen and (${devices.mobile}) {
      justify-content: center;
      font-size: .65em;
      padding: .5em 0;
    }
    @media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
      justify-content: center;
      font-size: .65em;
      padding: .5em 0;
    }
  }
  .contact-phone {
    display: flex;
    align-items: center;
    font-size: .55em;
    svg {
      flex-shrink: 0;
      font-size: 1em;
      padding-right: .7em;
    }
    @media screen and (${devices.mobile}) {
      justify-content: center;
      font-size: .65em;
      padding: .5em 0;
    }
    @media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
      justify-content: center;
      font-size: .65em;
      padding: .5em 0;
    }
  }
`;
// #endregion

// #region COMPONENT
const Press = ({ currentPath, currentLang }) => {

	const { t } = useTranslation('press');
	const scrollTo = useRef(null);

	// #region RENDER
	return (
		<PageWrapper
			title={t('meta.title')}
			description={t('meta.description')}
			introImageUrl="/src/public/img/press.jpg"
			alternateUrl={{ fr: '/presse', en: '/press', es: '/prensa' }}
			currentPath={currentPath}
			currentLang={currentLang}
		>
			<Head>
				{renderScript({
					'@context': 'https://schema.org',
					'@type': 'BreadcrumbList',
					'itemListElement': [{
						'@type': 'ListItem',
						'position': 1,
						'name': 'Accueil',
						'item': 'https://www.bruce.work/',
					}, {
						'@type': 'ListItem',
						'position': 2,
						'name': 'Presse',
						'item': 'https://www.bruce.work/presse/',
					}],
				})}
			</Head>
			<IntroSection underlineColor="blue" background="/img/press.jpg" extraSpace scrollTo={scrollTo}>
				<h1>{renderUnderline(t('introSection.h1', { returnObjects: true }))}</h1>
			</IntroSection>
			<SplitSection fixedHeight firstWidth={5} secondWidth={40} thirdWidth={5} fourthWidth={50} scrollTo={scrollTo}>
				<div />
				<SectionText>
					<Title underlineColor="red">
						{renderUnderline(t('previewPress.title', { returnObjects: true }))}
					</Title>
					<p>
						{t('previewPress.text')}
					</p>
					<Button
						href={t('previewPress.button.href')}
						name={t('previewPress.button.name')}
						version="whiteRed"
					/>
				</SectionText>
				<div />
				<PreviewPressContainer>
					{t('previewPress.articles', { returnObjects: true }).map((article, key) => (
						<PreviewPress
							key={key}
							title={article.title}
							date={article.date}
							logo={article.logo}
							alt={article.alt}
							href={article.href}
							bgImage={article.img}
						/>
					))}
				</PreviewPressContainer>
			</SplitSection>
			<SplitSection bgColor="red" firstWidth={15} secondWidth={35} thirdWidth={5} fourthWidth={35} fithWidth={10}>
				<div />
				<SectionText>
					<Title white underlineColor="prune">
						{renderUnderline(t('mediaKit.title', { returnObjects: true }))}
					</Title>
					<p>
						{t('mediaKit.text')}
					</p>
					<Button
						href={t('mediaKit.button.href')}
						name={t('mediaKit.button.name')}
						version="redWhite"
					>
						<FiDownload />
					</Button>
				</SectionText>
				<div />
				<DownloadResourcesContainer>
					{t('mediaKit.resources', { returnObjects: true }).map((resource, key) => (
						<DownloadResourcesButton href={resource.url} key={key} download>
							<div>{resource.name}</div> <FiDownload />
						</DownloadResourcesButton>
					))}
				</DownloadResourcesContainer>
				<div />
			</SplitSection>
			<SplitSection fixedHeight bgImage="/src/public/img/bg_newspaper.jpg" firstWidth={10} secondWidth={35} thirdWidth={10} fourthWidth={35} fithWidth={5}>
				<div />
				<SectionText>
					<Title white underlineColor="blue">
						{renderUnderline(t('newspaper.title', { returnObjects: true }))}
					</Title>
				</SectionText>
				<div />
				<LogoPressContainer>
					{t('newspaper.logoPress', { returnObjects: true }).map((logo, key) => (
						<a href={logo.href} key={key} target="_blank" rel="noreferrer">
							<img src={logo.url} alt={logo.alt} />
						</a>
					))}
				</LogoPressContainer>
				<div />
			</SplitSection>
			<SplitSection fixedHeight firstWidth={10} secondWidth={40} thirdWidth={5} fourthWidth={30} fithWidth={15}>
				<div />
				<SectionText>
					<Title underlineColor="beige">
						{renderUnderline(t('contact.title', { returnObjects: true }))}
						<InfoContact>
							<div className="contact-name">{t('contact.name')}</div>
							<div>
								<div className="contact-mail"> <FiMail /> {t('contact.info.mail')}</div>
								<div className="contact-phone"> <FiPhone />{t('contact.info.phone')}</div>
							</div>
						</InfoContact>
					</Title>
				</SectionText>
				<div />
				<BackgroundImage url="/img/press_contact.jpg" hideOnMobile />
				<BackgroundImage url="/img/bruce-heart.png" bgColor="beige" imageSize="60%" hideOnMobile />
			</SplitSection>
		</PageWrapper>
	);
	// #endregion
};
// #endregion

// #region CONNEXION
export const getServerSideProps = async ({ query }) => {
	return {
		props: {
			...await serverSideTranslations(query.language, ['press', 'navbar', 'footer'], nextI18NextConfig),
		},
	};
};
// #endregion

export default Press;
